import { Box, Spinner, Stack, Text } from '@spaceship-fspl/components';
import { DatedBalance } from '@spaceship-fspl/graphql';
import { formatDate, getToday, TimeScale } from '@spaceship-fspl/helpers';
import { match, rgba, text } from '@spaceship-fspl/styles';
import { TabSwitcher } from 'components';
import { LineGraph } from 'components/line-graph';
import { subMonths } from 'date-fns';
import React from 'react';
import styled from 'styled-components';

interface CompanyGraphProps {
  prices?: Array<DatedBalance>;
  timeScale: TimeScale;
  onTimeScaleChange: (value: TimeScale) => void;
  isLoading?: boolean;
}

export const filterByTimeScale = (
  prices: Array<DatedBalance> | undefined,
  timeScale: TimeScale,
): Array<DatedBalance> => {
  const dateFrom =
    timeScale === TimeScale.MAX
      ? new Date(0)
      : subMonths(getToday(), timeScale);

  return prices?.filter(({ date }) => date && new Date(date) >= dateFrom) ?? [];
};

const DateTextWrapper = styled(Text)`
  color: ${rgba('neutral.000', 0.6)};
  ${match('lg')`
    ${text({ variant: 3 })}
  `};
`;

const DateText: React.FC<
  React.PropsWithChildren<{ value: Date; timeScale: TimeScale }>
> = ({ value, timeScale }): React.ReactElement => {
  if (!value || !(value instanceof Date) || isNaN(value.getTime())) {
    return <DateTextWrapper variant={4}>-</DateTextWrapper>;
  }

  const format =
    timeScale === TimeScale.ONE_MONTH || timeScale === TimeScale.THREE_MONTHS
      ? 'dd MMM' // e.g. "15 Mar"
      : 'MMM yyyy'; // e.g. "Mar 2025"

  return (
    <DateTextWrapper variant={4}>
      {formatDate(value, format).toUpperCase()}
    </DateTextWrapper>
  );
};

const GRAPH_TAB_OPTIONS = [
  { label: '1M', value: TimeScale.ONE_MONTH },
  {
    label: '3M',
    value: TimeScale.THREE_MONTHS,
  },
  {
    label: '6M',
    value: TimeScale.SIX_MONTHS,
  },
  {
    label: '12M',
    value: TimeScale.TWELVE_MONTHS,
  },
  { label: 'MAX', value: TimeScale.MAX },
];

export const CompanyGraph: React.FC<
  React.PropsWithChildren<CompanyGraphProps>
> = ({ prices, timeScale, onTimeScaleChange, isLoading }) => {
  const filteredPrices = filterByTimeScale(prices, timeScale);

  if (isLoading || prices?.length === 0) {
    return (
      <Box position="absolute" top="50%" left="50%">
        <Spinner />
      </Box>
    );
  }

  return (
    <Stack spaceY="md">
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height={{ sm: 220, lg: 400 }}
      >
        <LineGraph
          data={filteredPrices}
          xAccessor={({ date }) => Date.parse(date)}
          yAccessor={({ audAmount }) => Number(audAmount)}
          renderDate={(date) => <DateText value={date} timeScale={timeScale} />}
          timeScale={timeScale}
        />
      </Box>
      <TabSwitcher
        value={timeScale}
        onChange={(value) => onTimeScaleChange(value)}
        options={GRAPH_TAB_OPTIONS}
        spaceX={{ xs: 'sm', md: 'sm', lg: 'lg' }}
      />
    </Stack>
  );
};
